/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { ActivityIndicator, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { webDarkScrollProps, webDarkScrollStyle } from '../../../shared/theme/webScrollbar';
import { styles } from '../posShared.styles';
import { OperationalHintIcon } from '../../../shared/ui/OperationalHintIcon';
import type { CartItem } from '../../store/usePosStore';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import { formatPromoMoney, summarizeCheckoutLines } from '../../../promotions/public-api';
import { formatQtyCompact } from '@shared/quantity';
import {
  CART_LINE_STOCK_HINT,
  formatCheckoutStockIssuesSummary,
  getStockWarningSeverity,
  mapStockWarningsByProductId,
} from '../../application/posCartStockIssuePresentation';
import type { PosStockWarningDetail } from '../../domain/posTypes';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';

type Props = {
  cart?: CartItem[];
  cartEvaluation?: CartEvaluationResult | null;
  layoutWide: boolean;
  detailExpanded: boolean;
  onToggleDetail: () => void;
  stockWarningsLoading?: boolean;
  stockCanProceed?: boolean;
  stockWarningDetails?: PosStockWarningDetail[];
  hasBlockingStockIssue?: boolean;
  hasExpiredStock?: boolean;
};

export function PosCheckoutDetailPanel(props: Props) {
  const ctx = usePosCheckoutContextOptional();
  const {
    cart = [],
    cartEvaluation = null,
    layoutWide,
    detailExpanded,
    onToggleDetail,
    stockWarningsLoading = false,
    stockCanProceed = true,
    stockWarningDetails = [],
    hasBlockingStockIssue = false,
    hasExpiredStock = false,
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const checkoutLineRows = useMemo(
    () => summarizeCheckoutLines(cart, cartEvaluation),
    [cart, cartEvaluation]
  );
  const stockWarningByProductId = useMemo(
    () => mapStockWarningsByProductId(stockWarningDetails),
    [stockWarningDetails]
  );
  const checkoutStockSummary = useMemo(
    () => formatCheckoutStockIssuesSummary(stockWarningDetails),
    [stockWarningDetails]
  );

  const detailTable = (
    <View
      style={[styles.checkoutDetailTableBody, layoutWide && styles.checkoutDetailTableBodyWide]}
    >
      <View style={styles.checkoutDetailTableHeader}>
        <Text
          style={[
            styles.checkoutDetailHeaderCell,
            styles.checkoutDetailColProduct,
          ]}
        >
          {'\u00cdtem'}
        </Text>
        <View style={styles.checkoutDetailColHint} />
        <Text style={[styles.checkoutDetailHeaderCell, styles.checkoutDetailColQty]}>Cant</Text>
        <Text
          style={[styles.checkoutDetailHeaderCell, styles.checkoutDetailCellMoney, layoutWide && styles.checkoutDetailCellMoneyWide]}
          numberOfLines={1}
        >
          Importe
        </Text>
      </View>
      <ScrollView
        {...webDarkScrollProps()}
        style={[styles.checkoutDetailLinesScroll, webDarkScrollStyle]}
        nestedScrollEnabled
        showsVerticalScrollIndicator
      >
        {checkoutLineRows.map((row, index) => {
          const cartItem = cart.find((item) => item.lineId === row.lineId);
          const stockWarning = cartItem
            ? stockWarningByProductId.get(cartItem.productId)
            : undefined;
          return (
            <View
              key={row.lineId}
              style={[
                styles.checkoutDetailTableRow,
                index === checkoutLineRows.length - 1 && styles.checkoutDetailTableRowLast,
              ]}
            >
              <View style={styles.checkoutDetailProductCol}>
                <Text
                  style={[
                    styles.checkoutDetailCell,
                    styles.checkoutDetailCellProduct,
                    styles.checkoutDetailProductNameText,
                  ]}
                  numberOfLines={2}
                >
                  {cartItem?.name ?? `\u00cdtem ${index + 1}`}
                </Text>
              </View>
              <View style={styles.checkoutDetailColHint}>
                {stockWarning ? (
                  <OperationalHintIcon
                    title="Stock"
                    message={CART_LINE_STOCK_HINT}
                    severity={getStockWarningSeverity(stockWarning)}
                    size={15}
                  />
                ) : null}
              </View>
              <Text style={[styles.checkoutDetailCell, styles.checkoutDetailColQty]}>
                {typeof row.quantity === 'number'
                  ? formatQtyCompact(row.quantity, cartItem?.unitOfMeasure ?? 'unit')
                  : row.quantity}
              </Text>
              <View
                style={[
                  styles.checkoutDetailCellMoney,
                  layoutWide && styles.checkoutDetailCellMoneyWide,
                  styles.checkoutDetailMoneyCellStack,
                ]}
              >
                <Text style={[styles.checkoutDetailCell, styles.checkoutDetailNet]} numberOfLines={1}>
                  {formatPromoMoney(row.netTotal)}
                </Text>
                {row.discountTotal > 0 ? (
                  <Text style={styles.checkoutDetailWas} numberOfLines={1}>
                    {formatPromoMoney(row.listTotal)}
                  </Text>
                ) : null}
              </View>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );

  const ticketRows = (
    <View style={styles.checkoutAccBody}>
      {checkoutLineRows.map((row, index) => {
        const cartItem = cart.find((item) => item.lineId === row.lineId);
        const qtyPrefix =
          typeof row.quantity === 'number' && row.quantity !== 1
            ? `${formatQtyCompact(row.quantity, cartItem?.unitOfMeasure ?? 'unit')} \u00d7 `
            : '';
        return (
          <View key={row.lineId} style={styles.checkoutAccRow}>
            <Text style={styles.checkoutAccRowLabel} numberOfLines={2}>
              {qtyPrefix}
              {cartItem?.name ?? `\u00cdtem ${index + 1}`}
            </Text>
            <Text style={styles.checkoutAccRowValue}>{formatPromoMoney(row.listTotal)}</Text>
          </View>
        );
      })}
      {(cartEvaluation?.discountTotal ?? 0) > 0 ? (
        (() => {
          const byName = new Map<string, number>();
          const push = (name: string | undefined, amount: number) => {
            const key = (name ?? '').trim() || 'Descuentos';
            byName.set(key, (byName.get(key) ?? 0) + amount);
          };
          for (const line of cartEvaluation?.lines ?? []) {
            for (const d of line.appliedDiscounts ?? []) push(d.promotionName, d.discountAmount);
          }
          for (const d of cartEvaluation?.cartDiscounts ?? []) push(d.promotionName, d.discountAmount);
          const names = Array.from(byName.entries());
          return names.map(([name, amount], i) => (
            <View
              key={`disc-${i}`}
              style={[styles.checkoutAccRow, i === 0 && styles.checkoutAccRowDivider]}
            >
              <Text style={styles.checkoutAccRowLabel} numberOfLines={1}>
                {name}
              </Text>
              <Text style={[styles.checkoutAccRowValue, styles.checkoutDetailDiscount]}>
                {`\u2212${formatPromoMoney(amount)}`}
              </Text>
            </View>
          ));
        })()
      ) : null}
    </View>
  );

  if (cart.length === 0) return null;

  const stockHeadIcon = stockWarningsLoading ? (
    <ActivityIndicator size="small" color={ticketColors.gold} />
  ) : stockWarningDetails.length > 0 ? (
    <OperationalHintIcon
      title={
        !stockCanProceed
          ? 'Stock insuficiente'
          : hasExpiredStock
            ? 'Productos con vencimiento'
            : 'Stock a revisar'
      }
      message={checkoutStockSummary}
      severity={hasBlockingStockIssue ? 'critical' : 'warning'}
      size={16}
      testID="pos-checkout-stock-summary-hint"
    />
  ) : null;

  // Desktop: título estático + tabla siempre visible (proto). Mobile: accordion colapsable.
  if (layoutWide) {
    return (
      <View style={styles.checkoutSectionBlock}>
        <View style={styles.checkoutDetailTitleRow}>
          <Text style={styles.checkoutSecTitle}>Detalle del ticket</Text>
          {stockHeadIcon}
        </View>
        {detailTable}
      </View>
    );
  }

  return (
    <View style={[styles.checkoutSectionBlock, styles.checkoutAccordion]}>
      <View style={styles.checkoutAccHead}>
        <TouchableOpacity
          style={styles.checkoutDetailToggleLabel}
          onPress={onToggleDetail}
          accessibilityRole="button"
          accessibilityState={{ expanded: detailExpanded }}
          accessibilityLabel="Detalle del ticket"
        >
          <Text style={styles.checkoutAccHeadText}>Detalle del ticket</Text>
        </TouchableOpacity>
        {stockHeadIcon}
        <TouchableOpacity
          onPress={onToggleDetail}
          accessibilityRole="button"
          accessibilityLabel={detailExpanded ? 'Ocultar detalle' : 'Mostrar detalle'}
          hitSlop={8}
        >
          <Ionicons
            name={detailExpanded ? 'chevron-up' : 'chevron-down'}
            size={14}
            color={ticketColors.ink3}
          />
        </TouchableOpacity>
      </View>
      {detailExpanded ? ticketRows : null}
    </View>
  );
}
